import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExtensionIcon from '@mui/icons-material/Extension';
import { alpha, useTheme } from '@mui/material/styles';
import Link from 'components/common/Link';
import Modal from 'components/common/Modal';
import { beginExtensionInstallCheck, useExtensionDetection, FIREFOX_EXTENSION_SETTINGS_HREF } from 'lib/useExtensionDetection';
import { CommonContent, onboardingStepTextSx } from './constants';
import type { OnboardingTask } from './onboarding';
import type { RenderComponentProps } from './onboarding';

export const DOWNLOAD_EXTENSION_TASK_ID = 'download_extension';

const ExtensionInstallationContent = () => {
  const theme = useTheme();
  const { installed, browser, storeUrl } = useExtensionDetection();
  const [isCheckingInstall, setIsCheckingInstall] = useState(false);
  const isSafari = browser === 'safari';
  const isFirefox = browser === 'firefox';
  const firefoxInstallViaStore = isFirefox && Boolean(storeUrl.trim());

  useEffect(() => {
    if (installed) {
      setIsCheckingInstall(false);
    }
  }, [installed]);

  const handleStoreLinkClick = () => {
    if (isFirefox) {
      return;
    }

    setIsCheckingInstall(true);
    beginExtensionInstallCheck();
  };

  const showStoreLink = !isSafari || Boolean(storeUrl);
  const showInstallStatus = !isFirefox && (isCheckingInstall || installed);

  const chromeSteps = [
    {
      text: (
        <>
          Go to{' '}
          <Link href={storeUrl} target="_blank" rel="noopener noreferrer" onClick={handleStoreLinkClick}>
            Chrome Web Store
          </Link>
        </>
      ),
    },
    {
      text: 'Click "Add to Chrome" button',
    },
    {
      text: 'Confirm the installation when prompted',
    },
    {
      text: 'Pin the extension to your toolbar for easy access',
    },
  ];

  const safariStoreSteps = [
    {
      text: (
        <>
          Go to the{' '}
          <Link href={storeUrl} target="_blank" rel="noopener noreferrer" onClick={handleStoreLinkClick}>
            Mac App Store
          </Link>
        </>
      ),
    },
    {
      text: 'Click "Get" to install Harbor',
    },
    {
      text: 'Open Safari Settings → Extensions and turn on Harbor',
    },
    {
      text: 'Allow website access, then sign in from the Harbor toolbar button',
    },
  ];

  const safariManualSteps = [
    {
      text: (
        <>
          Harbor for Safari is coming to the Mac App Store. Until then, you can{' '}
          <Link href="/settings/candidates?tab=4" onClick={handleStoreLinkClick}>
            install it manually
          </Link>
          .
        </>
      ),
    },
    {
      text: 'Download the Safari extension file and load it in Safari as a temporary extension',
    },
    {
      text: 'In Safari Settings → Extensions, turn on Harbor and allow website access',
    },
    {
      text: 'Sign in from the Harbor toolbar button',
    },
  ];

  const firefoxStoreSteps = [
    {
      text: (
        <>
          Go to{' '}
          <Link href={storeUrl} target="_blank" rel="noopener noreferrer">
            Firefox Add-ons
          </Link>
        </>
      ),
    },
    {
      text: 'Click "Add to Firefox" button',
    },
    {
      text: 'Confirm the installation when prompted',
    },
    {
      text: 'Pin the extension to your toolbar for easy access',
    },
  ];

  const firefoxManualSteps = [
    {
      text: (
        <>
          Open <Link href={FIREFOX_EXTENSION_SETTINGS_HREF}>Settings → Candidates → Extension</Link>
        </>
      ),
    },
    {
      text: 'Click Download Extension and save the file.',
    },
    {
      text: 'In Firefox, open about:debugging and click This Firefox.',
    },
    {
      text: 'Click Load Temporary Add-on and select the downloaded Harbor extension file.',
    },
    {
      text: 'If prompted, allow Harbor access to findharbor.com, then pin it to your toolbar.',
    },
  ];

  const steps = isFirefox
    ? firefoxInstallViaStore
      ? firefoxStoreSteps
      : firefoxManualSteps
    : !isSafari
      ? chromeSteps
      : showStoreLink
        ? safariStoreSteps
        : safariManualSteps;

  const waitingCopy = isFirefox
    ? firefoxInstallViaStore
      ? 'Install Harbor from Firefox Add-ons, then return to this page. We will detect it automatically.'
      : 'Load the extension in Firefox, then return to this page. We will detect it automatically.'
    : !isSafari
      ? 'Install Harbor in the Chrome Web Store tab, then return to this page. We will detect it automatically.'
      : showStoreLink
        ? 'Install Harbor from the Mac App Store, then return to this page. We will detect it automatically.'
        : 'Load the extension in Safari, then return to this page. We will detect it automatically.';

  return (
    <Box>
      <CommonContent
        alert={<>Our Browser Extension adds candidates from LinkedIn to Harbor with one click.</>}
        version="102"
        steps={steps}
      />

      {showInstallStatus && (
        <Alert
          severity={installed ? 'success' : 'info'}
          icon={
            installed ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 28 }} />
            ) : (
              <CircularProgress size={28} thickness={4} color="inherit" />
            )
          }
          sx={{
            mt: 3,
            py: 2,
            px: 2.5,
            alignItems: installed ? 'center' : 'flex-start',
            border: 2,
            borderColor: installed ? 'success.main' : 'primary.main',
            bgcolor: installed
              ? alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.16 : 0.1)
              : alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.28 : 0.1),
            boxShadow: installed
              ? `0 0 0 1px ${alpha(theme.palette.success.main, 0.25)}`
              : `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
            '& .MuiAlert-icon': {
              mr: 1.5,
              mt: installed ? 0 : 0.25,
              p: 0,
              opacity: 1,
            },
            '& .MuiAlert-message': {
              width: '100%',
              py: installed ? 0 : 0.25,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: installed ? 0 : 0.75 }}>
            {!installed && <ExtensionIcon sx={{ fontSize: 22, color: 'primary.main' }} />}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
              {installed ? 'Extension detected!' : 'Waiting for extension installation'}
            </Typography>
          </Box>
          {!installed && (
            <Typography variant="body2" sx={{ ...onboardingStepTextSx, color: 'text.primary' }}>
              {waitingCopy}
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );
};

function ExtensionInstallGuideModal({ onClose }: RenderComponentProps) {
  return (
    <Modal
      open={true}
      title="Install Browser Extension"
      renderContent={() => <ExtensionInstallationContent />}
      onClose={onClose}
      hideCloseButton
    />
  );
}

ExtensionInstallGuideModal.displayName = 'ExtensionInstallGuideModal';

export const downloadExtensionOnboardingTask: OnboardingTask = {
  id: DOWNLOAD_EXTENSION_TASK_ID,
  title: 'Install Browser Extension',
  completed: false,
  renderComponent: ExtensionInstallGuideModal,
};

/** Auto-complete the download extension onboarding task when the browser extension is detected. */
export function useDownloadExtensionAutoComplete(
  tasks: OnboardingTask[],
  markTasksCompleted: (taskIds: string[]) => Promise<boolean>,
) {
  const { installed: extensionInstalled } = useExtensionDetection();
  const extensionAutoCompleteAttemptedRef = useRef(false);

  useEffect(() => {
    if (!extensionInstalled) {
      extensionAutoCompleteAttemptedRef.current = false;
      return;
    }

    if (extensionAutoCompleteAttemptedRef.current) {
      return;
    }

    const downloadTask = tasks.find(task => task.id === DOWNLOAD_EXTENSION_TASK_ID);
    if (!downloadTask || (downloadTask.completed && extensionInstalled)) {
      return;
    }

    extensionAutoCompleteAttemptedRef.current = true;
    void markTasksCompleted([DOWNLOAD_EXTENSION_TASK_ID]).then(success => {
      if (!success) {
        extensionAutoCompleteAttemptedRef.current = false;
      }
    });
  }, [extensionInstalled, tasks, markTasksCompleted]);
}
